import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const InteractiveTutorial = ({ user, onComplete, steps }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const isCoach = user?.role === 'coach';

  // Default steps based on role if none provided
  const defaultCoachSteps = [
    {
      title: "Your Coach Dashboard",
      description: "This is your command center. Use the tabs at the top to navigate between Templates, Exercise Library, Athletes, and Feedback.",
      highlight: "top-nav",
      position: "bottom"
    },
    {
      title: "Workout Templates",
      description: "Create reusable workout templates here. Define intensity types (Easy, Tempo, Hard) and prescriptions that you can assign to any athlete.",
      highlight: "templates-tab",
      position: "bottom"
    },
    {
      title: "Exercise Library",
      description: "Build your reference library with exercises, demo videos, and coaching tips. Athletes can browse this to learn proper technique.",
      highlight: "library-tab",
      position: "bottom"
    },
    {
      title: "Manage Athletes",
      description: "Click on any athlete to view their schedule, edit their workouts, and see their feedback. You can create schedules week by week.",
      highlight: "athletes-tab",
      position: "bottom"
    },
    {
      title: "Track Feedback",
      description: "See notes and feedback from your athletes after they complete workouts. This helps you adjust training based on how they're feeling.",
      highlight: "feedback-tab",
      position: "bottom"
    }
  ];

  const defaultAthleteSteps = [
    {
      title: "Your Training Schedule",
      description: "This is your personalized weekly training plan. Your coach has designed each workout specifically for you.",
      highlight: "schedule-section",
      position: "bottom"
    },
    {
      title: "Complete Workouts",
      description: "Check the box ✅ when you finish a workout. This lets your coach track your progress.",
      highlight: "checkbox-example",
      position: "right"
    },
    {
      title: "Add Feedback",
      description: "Use the notes section 💬 to tell your coach how the workout felt. Too easy? Too hard? Let them know!",
      highlight: "notes-section",
      position: "right"
    },
    {
      title: "Mark Personal Bests",
      description: "Hit a PR? Click the star ⭐ to mark it as a Personal Best. Track your progress over time!",
      highlight: "star-button",
      position: "right"
    },
    {
      title: "Exercise Library",
      description: "Not sure how to do an exercise? Check the Exercise Library for demo videos and coaching tips from your coach.",
      highlight: "library-link",
      position: "bottom"
    }
  ];

  const tutorialSteps = steps || (isCoach ? defaultCoachSteps : defaultAthleteSteps);
  const step = tutorialSteps[currentStep];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Mark tutorial as completed
    localStorage.setItem('tutorial_completed', 'true');
    onComplete();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.75)',
      zIndex: 9998,
      pointerEvents: 'auto'
    }}>
      {/* Tutorial Card */}
      <div style={{
        position: 'fixed',
        bottom: '40px',
        right: '40px',
        background: 'linear-gradient(135deg, #1f1f1f 0%, #2d2d2d 100%)',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        border: '2px solid #ef4444',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
        zIndex: 9999
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: '16px'
        }}>
          <div>
            <div style={{
              fontSize: '10px',
              fontWeight: '900',
              color: '#ef4444',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '8px'
            }}>
              Step {currentStep + 1} of {tutorialSteps.length}
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '900',
              color: 'white',
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {step.title}
            </h3>
          </div>
          <button
            onClick={handleSkip}
            style={{
              padding: '6px',
              background: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              transition: 'color 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ef4444'}
            onMouseLeave={(e) => e.target.style.color = '#6b7280'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <p style={{
          fontSize: '14px',
          color: '#d1d5db',
          lineHeight: '1.6',
          marginBottom: '20px',
          fontWeight: '500'
        }}>
          {step.description}
        </p>

        {/* Progress Dots */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          {tutorialSteps.map((_, index) => (
            <div
              key={index}
              style={{
                width: index === currentStep ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index === currentStep 
                  ? 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)'
                  : '#374151',
                transition: 'all 0.3s'
              }}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '10px',
                border: '2px solid #374151',
                background: 'transparent',
                color: '#9ca3af',
                fontWeight: '900',
                fontSize: '13px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#ef4444';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#374151';
                e.target.style.color = '#9ca3af';
              }}
            >
              <ChevronLeft size={16} />
              Back
            </button>
          )}
          
          <button
            onClick={handleNext}
            style={{
              flex: 2,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)',
              color: 'white',
              fontWeight: '900',
              fontSize: '13px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            {currentStep < tutorialSteps.length - 1 ? (
              <>
                Next
                <ChevronRight size={16} />
              </>
            ) : (
              '✓ Got it!'
            )}
          </button>
        </div>

        {/* Skip Link */}
        <button
          onClick={handleSkip}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '8px',
            background: 'transparent',
            border: 'none',
            color: '#6b7280',
            fontSize: '12px',
            fontWeight: '700',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.color = '#9ca3af'}
          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
        >
          Skip Tutorial
        </button>
      </div>
    </div>
  );
};

export default InteractiveTutorial;
